import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme as useMuiTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  UserPlusIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  PhoneIcon,
  PlusIcon,
  XMarkIcon,
  PencilSquareIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { staffPaymentApi } from '../../api/staffPaymentApi';
import { formatCurrency } from '../../config/currency';
import Modal from '../../components/Modal';
import { CURRENCY } from '../../config/currency';
import StaffPaymentHistory, { StaffPayment } from '../../components/finance/StaffPaymentHistory';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilterParams } from '../../hooks/useFilterParams';
import { financeApi } from '../../api/financeApi';
import { downloadBlob } from '../../utils/downloadBlob';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';
import TelegramAccessPanel from '../../components/staff/TelegramAccessPanel';
import PaymentModeSelect from '../../components/finance/PaymentModeSelect';
import { isoWeeksInMonth } from '../../utils/isoWeek';
import { todayLocalIso } from '../../utils/dateLocal';
import {
  PrimaryButton,
  FormActions,
  AppIconButton,
  PageHeroBand,
  CardContainer,
  FormInput,
  EmptyState,
} from '../../components/ui';

// --- Types ---

interface StaffMember {
  id: number;
  name: string;
  role?: string;
  phone?: string;
  agreedSalary?: number;
}

interface StaffSummaryRow {
  staffId: number;
  staffName: string;
  staffRole?: string;
  salary: number;
  advance: number;
  deduction: number;
  bonus: number;
  overtime: number;
  netTotal: number;
  outstandingAdvance: number;
}

interface StaffFormData {
  name: string;
  role: string;
  agreedSalary: number | '';
  phone: string;
}

type PeriodOption = 'THIS_MONTH' | 'LAST_MONTH' | 'CUSTOM';

// --- Constants ---

const PERIOD_OPTIONS: { value: PeriodOption; label: string }[] = [
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'LAST_MONTH', label: 'Last Month' },
  { value: 'CUSTOM', label: 'Custom' },
];

const DEFAULT_ROLES = [
  'Chef',
  'Sous Chef',
  'Kitchen Helper',
  'Server',
  'Bartender',
  'Host',
  'Cashier',
  'Manager',
  'Cleaner',
  'Delivery',
];

const getDefaultStaffForm = (): StaffFormData => ({
  name: '',
  role: '',
  agreedSalary: '',
  phone: '',
});

const fmtLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getPeriodDates = (period: PeriodOption): { dateFrom: string; dateTo: string } => {
  const now = new Date();
  if (period === 'THIS_MONTH') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      dateFrom: fmtLocal(start),
      dateTo: fmtLocal(now),
    };
  }
  if (period === 'LAST_MONTH') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      dateFrom: fmtLocal(start),
      dateTo: fmtLocal(end),
    };
  }
  return { dateFrom: '', dateTo: '' };
};

// --- Component ---

const StaffPayments: React.FC = () => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // Data state
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [summaryRows, setSummaryRows] = useState<StaffSummaryRow[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffFilters, setStaffFilters] = useFilterParams(
    { period: 'THIS_MONTH', customDateFrom: '', customDateTo: '' },
    { prefix: 'staff_' },
  );
  const period = staffFilters.period as PeriodOption;
  const customDateFrom = staffFilters.customDateFrom;
  const customDateTo = staffFilters.customDateTo;
  const setPeriod = useCallback(
    (v: PeriodOption) => setStaffFilters({ period: v }),
    [setStaffFilters],
  );
  const setCustomDateFrom = useCallback(
    (v: string) => setStaffFilters({ customDateFrom: v }),
    [setStaffFilters],
  );
  const setCustomDateTo = useCallback(
    (v: string) => setStaffFilters({ customDateTo: v }),
    [setStaffFilters],
  );
  const [expandedStaffId, setExpandedStaffId] = useState<number | null>(null);

  // Add/Edit Staff modal
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState<number | null>(null);
  const [staffForm, setStaffForm] = useState<StaffFormData>(getDefaultStaffForm());
  const [staffFormErrors, setStaffFormErrors] = useState<Record<string, string>>({});
  const [savingStaff, setSavingStaff] = useState(false);

  // Record Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStaff, setPaymentStaff] = useState<StaffMember | null>(null);
  const [paymentStaffId, setPaymentStaffId] = useState<number | ''>('');
  const [paymentType, setPaymentType] = useState<string>('SALARY');
  const [paymentAmount, setPaymentAmount] = useState<number | ''>('');
  const [paymentMode, setPaymentMode] = useState<string>('CASH');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentDate, setPaymentDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [paymentSalaryMonth, setPaymentSalaryMonth] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const [paymentSalaryFrequency, setPaymentSalaryFrequency] = useState<'MONTHLY' | 'WEEKLY'>('MONTHLY');
  const [paymentSalaryWeek, setPaymentSalaryWeek] = useState<number | ''>('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // Expense categories (for resolving SALARY/ADVANCE/etc to a category id when recording payments)
  const [expenseCategories, setExpenseCategories] = useState<Array<{ id: number; name: string; isStaffCategory?: boolean }>>([]);

  // Map UI payment type -> seeded expense category name
  const TYPE_TO_CATEGORY_NAME: Record<string, string> = {
    SALARY: 'Salaries & Wages',
    ADVANCE: 'Staff Advance',
    BONUS: 'Staff Bonus',
    OVERTIME: 'Staff Overtime',
    DEDUCTION: 'Staff Deduction',
  };
  // Inverse — recover payment type from category name for edit pre-fill.
  const CATEGORY_NAME_TO_TYPE: Record<string, string> = Object.fromEntries(
    Object.entries(TYPE_TO_CATEGORY_NAME).map(([k, v]) => [v, k])
  );

  // Export state
  const [exporting, setExporting] = useState(false);

  // --- Computed ---

  const { dateFrom, dateTo } = useMemo(() => {
    if (period === 'CUSTOM') {
      return { dateFrom: customDateFrom, dateTo: customDateTo };
    }
    return getPeriodDates(period);
  }, [period, customDateFrom, customDateTo]);

  const grandTotal = useMemo(
    () => summaryRows.reduce((sum, row) => sum + row.netTotal, 0),
    [summaryRows]
  );

  // Lookup table so we can fall back to staff list for role / phone / agreedSalary
  // when the summary endpoint omits them.
  const staffById = useMemo(() => {
    const map = new Map<number, StaffMember>();
    staff.forEach((s) => map.set(s.id, s));
    return map;
  }, [staff]);

  const resolveRole = useCallback(
    (row: StaffSummaryRow): string => row.staffRole || staffById.get(row.staffId)?.role || '',
    [staffById]
  );

  // --- Data Fetching ---

  const fetchStaff = useCallback(async () => {
    try {
      const res = await staffPaymentApi.getStaff();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setStaff(data);
    } catch (err: unknown) {
      console.error('Error fetching staff:', err);
    }
  }, []);

  const fetchExpenseCategories = useCallback(async () => {
    try {
      const res = await financeApi.getCategories();
      const data = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      setExpenseCategories(data);
    } catch (err: unknown) {
      console.error('Error fetching expense categories:', err);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const params: Record<string, unknown> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await staffPaymentApi.getStaffSummary(params);
      const data = res.data?.staff || (Array.isArray(res.data) ? res.data : []);
      setSummaryRows(data);
    } catch (err: unknown) {
      console.error('Error fetching staff summary:', err);
      toast.error('Failed to load staff summary');
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStaff(), fetchSummary(), fetchExpenseCategories()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) {
      fetchSummary();
    }
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Honor navigation from Expenses page: state.editPayment opens the edit modal
  // pre-filled with the expense being edited. Clears state so a refresh
  // doesn't re-trigger.
  useEffect(() => {
    if (loading) return;
    const state = location.state as { editPayment?: StaffPayment & { staffMemberId?: number } } | null;
    const pending = state?.editPayment;
    if (!pending) return;
    const staffMember = staff.find((s) => s.id === pending.staffMemberId) || null;
    setExpandedStaffId(pending.staffMemberId || null);
    openEditPaymentModal(staffMember, pending);
    navigate(location.pathname, { replace: true, state: {} });
  }, [loading, staff, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStaff(), fetchSummary()]);
    setRefreshing(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting...');
    try {
      const params: Record<string, unknown> = { format: 'CSV', dateFrom, dateTo };
      const res = await financeApi.exportData('staff-payments', params);
      const filename = `staff-payments-${todayLocalIso()}.csv`;
      downloadBlob(res.data, filename);
      toast.success('Downloaded!', { id: toastId });
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // --- Add Staff ---

  const handleStaffFormChange = (field: keyof StaffFormData, value: unknown) => {
    setStaffForm((prev) => ({ ...prev, [field]: value }));
    if (staffFormErrors[field]) {
      setStaffFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateStaffForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!staffForm.name.trim()) errors.name = 'Name is required';
    setStaffFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddStaffModal = () => {
    setEditingStaffId(null);
    setStaffForm(getDefaultStaffForm());
    setStaffFormErrors({});
    setAddStaffOpen(true);
  };

  const openEditStaffModal = (staffMember: StaffMember) => {
    setEditingStaffId(staffMember.id);
    setStaffForm({
      name: staffMember.name || '',
      role: staffMember.role || '',
      agreedSalary: staffMember.agreedSalary ?? '',
      phone: staffMember.phone || '',
    });
    setStaffFormErrors({});
    setAddStaffOpen(true);
  };

  const handleCloseStaffModal = () => {
    setAddStaffOpen(false);
    setEditingStaffId(null);
    setStaffForm(getDefaultStaffForm());
    setStaffFormErrors({});
  };

  const handleSaveStaff = async () => {
    if (!validateStaffForm()) return;
    setSavingStaff(true);
    try {
      const payload = {
        name: staffForm.name.trim(),
        role: staffForm.role.trim() || null,
        agreedSalary: staffForm.agreedSalary ? Number(staffForm.agreedSalary) : null,
        phone: staffForm.phone.trim() || null,
      };
      if (editingStaffId) {
        await staffPaymentApi.updateStaff(editingStaffId, payload);
        toast.success('Staff member updated');
      } else {
        await staffPaymentApi.createStaff(payload);
        toast.success('Staff member added');
      }
      handleCloseStaffModal();
      await Promise.all([fetchStaff(), fetchSummary()]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save staff member';
      toast.error(message);
    } finally {
      setSavingStaff(false);
    }
  };

  // --- Record Payment ---

  const openPaymentModal = (staffMember: StaffMember | null, type: string = 'SALARY') => {
    setEditingPaymentId(null);
    setPaymentStaff(staffMember);
    setPaymentStaffId(staffMember?.id ?? '');
    setPaymentType(type);
    setPaymentAmount('');
    setPaymentMode('CASH');
    setPaymentDescription('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentSalaryMonth(new Date().toISOString().slice(0, 7));
    setPaymentSalaryFrequency('MONTHLY');
    setPaymentSalaryWeek('');
    setPaymentModalOpen(true);
  };

  // Open the same Record Payment modal in "edit" mode for an existing payment.
  const openEditPaymentModal = (staffMember: StaffMember | null, payment: StaffPayment) => {
    const inferredType = (payment.type
      || (CATEGORY_NAME_TO_TYPE[(payment.categoryName || '').trim()] ?? 'SALARY')) as string;
    setEditingPaymentId(payment.id);
    setPaymentStaff(staffMember);
    setPaymentStaffId(staffMember?.id ?? '');
    setPaymentType(inferredType);
    setPaymentAmount(payment.amount);
    setPaymentMode(payment.paymentMode || 'CASH');
    setPaymentDescription(payment.description || '');
    setPaymentDate(payment.date);
    setPaymentSalaryMonth(payment.salaryMonth || new Date().toISOString().slice(0, 7));
    setPaymentSalaryFrequency(payment.salaryFrequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY');
    setPaymentSalaryWeek(payment.salaryWeek ?? '');
    setPaymentModalOpen(true);
  };

  const handleSavePayment = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const effectiveStaffId = paymentStaff?.id ?? (paymentStaffId === '' ? null : Number(paymentStaffId));
    if (!effectiveStaffId) {
      toast.error('Pick a staff member');
      return;
    }
    if (!paymentSalaryMonth || !/^\d{4}-(0[1-9]|1[0-2])$/.test(paymentSalaryMonth)) {
      toast.error('Pick the month this payment is for');
      return;
    }

    const targetCategoryName = TYPE_TO_CATEGORY_NAME[paymentType];
    const category = expenseCategories.find(
      (c) => c.name.toLowerCase() === (targetCategoryName || '').toLowerCase()
    );
    if (!category) {
      toast.error(`Could not find "${targetCategoryName}" category`);
      return;
    }

    if (paymentType === 'SALARY' && paymentSalaryFrequency === 'WEEKLY' && !paymentSalaryWeek) {
      toast.error('Pick the ISO week this weekly payment covers');
      return;
    }

    setSavingPayment(true);
    try {
      const payload: Record<string, unknown> = {
        categoryId: category.id,
        staffMemberId: effectiveStaffId,
        amount: Number(paymentAmount),
        date: paymentDate,
        paymentMode,
        paymentStatus: 'PAID',
        salaryMonth: paymentSalaryMonth,
        ...(paymentType === 'SALARY'
          ? {
              salaryFrequency: paymentSalaryFrequency,
              ...(paymentSalaryFrequency === 'WEEKLY' && paymentSalaryWeek
                ? { salaryWeek: Number(paymentSalaryWeek) }
                : {}),
            }
          : {}),
        description: paymentDescription || null,
      };
      const staffName = paymentStaff?.name
        || staff.find((s) => s.id === effectiveStaffId)?.name
        || 'staff';
      if (editingPaymentId) {
        await financeApi.updateExpense(editingPaymentId, payload);
        toast.success(`${paymentType.charAt(0) + paymentType.slice(1).toLowerCase()} updated for ${staffName}`);
      } else {
        await financeApi.createExpense(payload);
        toast.success(`${paymentType.charAt(0) + paymentType.slice(1).toLowerCase()} recorded for ${staffName}`);
      }
      setPaymentModalOpen(false);
      setEditingPaymentId(null);
      // Bump history refresh so the expanded panel re-fetches.
      setHistoryRefreshKey((k) => k + 1);
      await fetchSummary();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to record payment';
      toast.error(message);
    } finally {
      setSavingPayment(false);
    }
  };

  // --- Shared styles ---

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  // --- Skeleton ---

  const renderSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={isMobile ? 100 : 56}
          sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', borderRadius: '10px' }}
        />
      ))}
    </Box>
  );

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow="Finance · Staff"
        title="Staff Payments"
        data-testid="staff-payments-header"
        actions={
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5 }}>
            <AppIconButton
              icon={<ArrowDownTrayIcon style={{ width: 20, height: 20 }} />}
              tooltip="Export CSV"
              variant="secondary"
              onClick={handleExportCsv}
              disabled={exporting}
              sx={{ borderRadius: '10px', width: 40, height: 40 }}
            />
            <AppIconButton
              icon={<ArrowPathIcon style={{ width: 20, height: 20 }} className={refreshing ? 'animate-spin' : ''} />}
              tooltip="Refresh"
              variant="primary"
              onClick={handleRefresh}
              disabled={refreshing}
            />
            <PrimaryButton
              startIcon={<BanknotesIcon style={{ width: 20, height: 20 }} />}
              onClick={() => openPaymentModal(null, 'SALARY')}
              disabled={staff.length === 0}
            >
              Record Salary
            </PrimaryButton>
            <PrimaryButton
              startIcon={<UserPlusIcon style={{ width: 20, height: 20 }} />}
              onClick={openAddStaffModal}
            >
              Add Staff
            </PrimaryButton>
          </Box>
        }
      />

      {/* Period Selector */}
      <CardContainer disableHover sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {PERIOD_OPTIONS.map((opt) => {
            const isActive = period === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                onClick={() => setPeriod(opt.value)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.813rem',
                  minHeight: 36,
                  px: 0.5,
                  backgroundColor: isActive ? `${theme.primary}20` : theme.gray?.[100] || '#F0F0F0',
                  color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                  border: isActive ? `1.5px solid ${theme.primary}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
              />
            );
          })}

          {period === 'CUSTOM' && (
            <FinanceDateRange
              from={customDateFrom}
              to={customDateTo}
              onFromChange={setCustomDateFrom}
              onToChange={setCustomDateTo}
              showLabels
              sx={{ flex: '1 1 260px' }}
            />
          )}
        </Box>
      </CardContainer>

      {/* Filter-aware totals over the listed staff summaries for the
          selected period */}
      {!loading && summaryRows.length > 0 && (
        <FilteredTotals
          data-testid="staff-filtered-totals"
          items={[
            { label: 'Net Paid', value: formatCurrency(grandTotal, { showDecimals: false }) },
            {
              label: 'Advances Owed',
              value: formatCurrency(
                summaryRows.reduce((sum, row) => sum + (row.outstandingAdvance || 0), 0),
                { showDecimals: false },
              ),
            },
            { label: 'Staff', value: String(summaryRows.length) },
          ]}
        />
      )}

      {/* Staff Summary */}
      {loading ? (
        renderSkeleton()
      ) : staff.length === 0 && summaryRows.length === 0 ? (
        /* Empty state */
        <EmptyState
          icon={<UsersIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
          title="No staff members yet"
          subtitle="Add your team to track salaries, advances, and tips in one place."
          actionButton={
            <PrimaryButton
              startIcon={<UserPlusIcon style={{ width: 18, height: 18 }} />}
              onClick={openAddStaffModal}
            >
              Add Staff
            </PrimaryButton>
          }
        />
      ) : isMobile ? (
        /* Mobile: Card layout */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {summaryRows.map((row) => {
            const isExpanded = expandedStaffId === row.staffId;
            const staffMember = staff.find((s) => s.id === row.staffId) || { id: row.staffId, name: row.staffName };

            return (
              <CardContainer
                key={row.staffId}
                disableHover
              >
                {/* Staff card header */}
                <Box
                  onClick={() => setExpandedStaffId(isExpanded ? null : row.staffId)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:active': { transform: 'scale(0.99)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: theme.text }}>
                        {row.staffName}
                      </Typography>
                      {resolveRole(row) && (
                        <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                          {resolveRole(row)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.outstandingAdvance > 0 && (
                        <Chip
                          label={`Owed: ${formatCurrency(row.outstandingAdvance, { showDecimals: false })}`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 22,
                            backgroundColor: `${theme.danger || '#D99982'}20`,
                            color: theme.danger || '#D99982',
                            border: `1px solid ${theme.danger || '#D99982'}44`,
                          }}
                        />
                      )}
                      {isExpanded ? (
                        <ChevronUpIcon style={{ width: 20, height: 20, color: theme.gray?.[400] || '#999' }} />
                      ) : (
                        <ChevronDownIcon style={{ width: 20, height: 20, color: theme.gray?.[400] || '#999' }} />
                      )}
                    </Box>
                  </Box>

                  {/* Payment breakdown */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Salary', value: row.salary, color: 'var(--text-link)' },
                      { label: 'Advance', value: row.advance, color: 'var(--status-warn)' },
                      { label: 'Bonus', value: row.bonus, color: theme.success || '#4CAF50' },
                      { label: 'Overtime', value: row.overtime, color: '#9C27B0' }, /* basil-todo */
                      { label: 'Deduction', value: row.deduction, color: theme.danger || '#D99982' },
                    ]
                      .filter((item) => item.value > 0)
                      .map((item) => (
                        <Box key={item.label}>
                          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.6rem' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: item.color, fontSize: '0.8rem' }}>
                            {formatCurrency(item.value, { showDecimals: false })}
                          </Typography>
                        </Box>
                      ))}
                  </Box>

                  {/* Net Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: `1px solid ${theme.gray?.[100] || '#F0F0F0'}` }}>
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
                      Net Total
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.05rem' }}>
                      {formatCurrency(row.netTotal, { showDecimals: false })}
                    </Typography>
                  </Box>
                </Box>

                {/* Expanded: Quick actions + payment history */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: `1px solid ${theme.components?.card?.border || '#E0E0E0'}` }}>
                    {/* Quick action buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label="Edit"
                        onClick={() => openEditStaffModal(staffMember as StaffMember)}
                        icon={<PencilSquareIcon style={{ width: 14, height: 14 }} />}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          backgroundColor: `${theme.primary}15`,
                          color: theme.primary,
                          border: `1px solid ${theme.primary}44`,
                          '& .MuiChip-icon': { color: theme.primary },
                          '&:hover': { backgroundColor: `${theme.primary}25` },
                        }}
                      />
                      {['SALARY', 'ADVANCE', 'BONUS', 'OVERTIME', 'DEDUCTION'].map((type) => {
                        const style = {
                          SALARY: { label: 'Record Salary', color: 'var(--text-link)' },
                          ADVANCE: { label: 'Give Advance', color: 'var(--status-warn)' },
                          BONUS: { label: 'Record Bonus', color: theme.success || '#4CAF50' },
                          OVERTIME: { label: 'Overtime', color: '#9C27B0' }, /* basil-todo */
                          DEDUCTION: { label: 'Deduction', color: theme.danger || '#D99982' },
                        }[type]!;
                        return (
                          <Chip
                            key={type}
                            label={style.label}
                            onClick={() => openPaymentModal(staffMember as StaffMember, type)}
                            icon={<BanknotesIcon style={{ width: 14, height: 14 }} />}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              backgroundColor: `${style.color}15`,
                              color: style.color,
                              border: `1px solid ${style.color}44`,
                              '& .MuiChip-icon': { color: style.color },
                              '&:hover': { backgroundColor: `${style.color}25` },
                            }}
                          />
                        );
                      })}
                    </Box>

                    {/* Payment history */}
                    <StaffPaymentHistory
                      staffId={row.staffId}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                      refreshKey={historyRefreshKey}
                      onEditPayment={(payment) =>
                        openEditPaymentModal(staffMember as StaffMember, payment)
                      }
                    />
                  </Box>
                </Collapse>
              </CardContainer>
            );
          })}

          {/* Grand total card */}
          {summaryRows.length > 0 && (
            <CardContainer
              disableHover
              sx={{
                p: 2,
                borderColor: `${theme.primary}44`,
                backgroundColor: `${theme.primary}08`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                  Grand Total
                </Typography>
                <Typography sx={{ fontWeight: 800, color: theme.primary, fontSize: '1.2rem' }}>
                  {formatCurrency(grandTotal, { showDecimals: false })}
                </Typography>
              </Box>
            </CardContainer>
          )}
        </Box>
      ) : (
        /* Desktop: Table layout */
        <CardContainer disableHover>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Name', 'Role', 'Salary', 'Advance', 'Bonus', 'Overtime', 'Deduction', 'Net Total', 'Outstanding', ''].map((header) => (
                    <TableCell
                      key={header}
                      align={['Salary', 'Advance', 'Bonus', 'Overtime', 'Deduction', 'Net Total', 'Outstanding'].includes(header) ? 'right' : 'left'}
                      sx={{
                        fontWeight: 600,
                        color: theme.text,
                        borderBottom: `2px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                        backgroundColor: theme.components?.table?.header || theme.background,
                        fontSize: '0.813rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((row) => {
                  const isExpanded = expandedStaffId === row.staffId;
                  const staffMember = staff.find((s) => s.id === row.staffId) || { id: row.staffId, name: row.staffName };
                  return (
                    <React.Fragment key={row.staffId}>
                      <TableRow
                        hover
                        onClick={() => setExpandedStaffId(isExpanded ? null : row.staffId)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: `${theme.primary}08` } }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: theme.text }}>{row.staffName}</TableCell>
                        <TableCell sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                          {resolveRole(row) || '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'var(--text-link)', fontWeight: 600 }}>
                          {row.salary > 0 ? formatCurrency(row.salary, { showDecimals: false }) : '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'var(--status-warn)', fontWeight: 600 }}>
                          {row.advance > 0 ? formatCurrency(row.advance, { showDecimals: false }) : '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.success || '#4CAF50', fontWeight: 600 }}>
                          {row.bonus > 0 ? formatCurrency(row.bonus, { showDecimals: false }) : '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#9C27B0', fontWeight: 600 }}> {/* basil-todo */}
                          {row.overtime > 0 ? formatCurrency(row.overtime, { showDecimals: false }) : '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: theme.danger || '#D99982', fontWeight: 600 }}>
                          {row.deduction > 0 ? formatCurrency(row.deduction, { showDecimals: false }) : '--'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: theme.text, fontSize: '0.95rem' }}>
                          {formatCurrency(row.netTotal, { showDecimals: false })}
                        </TableCell>
                        <TableCell align="right">
                          {row.outstandingAdvance > 0 ? (
                            <Chip
                              label={formatCurrency(row.outstandingAdvance, { showDecimals: false })}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 24,
                                backgroundColor: `${theme.danger || '#D99982'}20`,
                                color: theme.danger || '#D99982',
                                border: `1px solid ${theme.danger || '#D99982'}44`,
                              }}
                            />
                          ) : (
                            <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontSize: '0.813rem' }}>--</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            <AppIconButton
                              icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />}
                              tooltip="Edit Staff"
                              variant="ghost"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditStaffModal(staffMember as StaffMember);
                              }}
                            />
                            <AppIconButton
                              icon={<BanknotesIcon style={{ width: 16, height: 16 }} />}
                              tooltip="Record Payment"
                              variant="ghost"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPaymentModal(staffMember as StaffMember);
                              }}
                              sx={{ color: theme.success, '&:hover': { backgroundColor: `${theme.success}18`, color: theme.success } }}
                            />
                            <AppIconButton
                              icon={isExpanded ? <ChevronUpIcon style={{ width: 16, height: 16 }} /> : <ChevronDownIcon style={{ width: 16, height: 16 }} />}
                              tooltip={isExpanded ? 'Collapse' : 'Expand'}
                              variant="ghost"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedStaffId(isExpanded ? null : row.staffId);
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0, borderBottom: 'none' }}>
                            <Collapse in={isExpanded}>
                              <Box sx={{ p: 2, backgroundColor: `${theme.primary}05` }}>
                                <StaffPaymentHistory
                                  staffId={row.staffId}
                                  staffName={row.staffName}
                                  dateFrom={dateFrom}
                                  dateTo={dateTo}
                                  refreshKey={historyRefreshKey}
                                  onEditPayment={(payment) =>
                                    openEditPaymentModal(staffMember as StaffMember, payment)
                                  }
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Grand total row */}
                {summaryRows.length > 0 && (
                  <TableRow sx={{ backgroundColor: `${theme.primary}08` }}>
                    <TableCell colSpan={7} sx={{ fontWeight: 700, color: theme.text, fontSize: '0.875rem' }}>
                      Grand Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: theme.primary, fontSize: '1.05rem' }}>
                      {formatCurrency(grandTotal, { showDecimals: false })}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContainer>
      )}

      {/* Telegram staff bot access — scoped invites for staff members */}
      <TelegramAccessPanel staff={staff.map((s) => ({ id: s.id, name: s.name }))} />

      {/* Mobile FAB — context-aware: Add Staff Member or Record Payment */}
      {isMobile && (
        <SpeedDial
          ariaLabel="Staff actions"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            zIndex: 1000,
            '& .MuiFab-primary': {
              backgroundColor: theme.button?.primary?.bg || 'var(--action)',
              color: theme.button?.primary?.text || '#FFFFFF',
              width: 56,
              height: 56,
              boxShadow: '0 4px 16px color-mix(in srgb, var(--action) 40%, transparent)',
              '&:hover': {
                backgroundColor: theme.button?.primary?.hover || 'var(--action-hover)',
                boxShadow: '0 6px 20px color-mix(in srgb, var(--action) 50%, transparent)',
              },
            },
          }}
          icon={
            <SpeedDialIcon
              icon={<PlusIcon style={{ width: 28, height: 28 }} />}
              openIcon={<XMarkIcon style={{ width: 28, height: 28 }} />}
            />
          }
        >
          <SpeedDialAction
            icon={<UserPlusIcon style={{ width: 22, height: 22 }} />}
            tooltipTitle="Add Staff Member"
            tooltipOpen
            onClick={openAddStaffModal}
            sx={{
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                whiteSpace: 'nowrap',
                fontWeight: 600,
                fontSize: '0.813rem',
                color: theme.text,
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              },
            }}
          />
          <SpeedDialAction
            icon={<BanknotesIcon style={{ width: 22, height: 22 }} />}
            tooltipTitle="Record Payment"
            tooltipOpen
            onClick={() => openPaymentModal(null, 'SALARY')}
            FabProps={{ disabled: staff.length === 0 }}
            sx={{
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                whiteSpace: 'nowrap',
                fontWeight: 600,
                fontSize: '0.813rem',
                color: theme.text,
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              },
            }}
          />
        </SpeedDial>
      )}

      {/* Add/Edit Staff Modal */}
      <Modal
        isOpen={addStaffOpen}
        onClose={handleCloseStaffModal}
        title={editingStaffId ? 'Edit Staff Member' : 'Add Staff Member'}
        subtitle={editingStaffId ? 'Update staff details' : 'Enter staff details'}
        maxWidth="sm"
        mobileFullScreen
        footer={
          <FormActions
            onCancel={handleCloseStaffModal}
            onSubmit={handleSaveStaff}
            submitLabel="Save"
            loading={savingStaff}
          />
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormInput
            label="Name *"
            value={staffForm.name}
            onChange={(e) => handleStaffFormChange('name', e.target.value)}
            error={Boolean(staffFormErrors.name)}
            helperText={staffFormErrors.name}
            fullWidth
            autoFocus
            placeholder="e.g. Ramesh Kumar"
          />
          <Autocomplete
            freeSolo
            value={staffForm.role}
            onChange={(_, val) => handleStaffFormChange('role', val ?? '')}
            onInputChange={(_, val) => handleStaffFormChange('role', val)}
            options={Array.from(new Set([
              ...DEFAULT_ROLES,
              ...staff.map((s) => s.role).filter((r): r is string => Boolean(r)),
            ])).sort()}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                placeholder="Pick or type — e.g. Chef, Server, Cashier"
                fullWidth
              />
            )}
          />
          <FormInput
            label="Agreed Salary"
            type="number"
            value={staffForm.agreedSalary}
            onChange={(e) => handleStaffFormChange('agreedSalary', e.target.value ? Number(e.target.value) : '')}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: theme.primary }}>{CURRENCY.symbol}</Typography>
                </InputAdornment>
              ),
            }}
            inputProps={{ min: 0 }}
          />
          <FormInput
            label="Phone"
            value={staffForm.phone}
            onChange={(e) => handleStaffFormChange('phone', e.target.value)}
            fullWidth
            placeholder="e.g. 9876543210"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon style={{ width: 18, height: 18, color: theme.gray?.[400] || '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Modal>

      {/* Record/Edit Payment Modal */}
      <Modal
        isOpen={paymentModalOpen}
        onClose={() => { setPaymentModalOpen(false); setEditingPaymentId(null); }}
        title={`${editingPaymentId ? 'Edit Payment' : 'Record Payment'}${paymentStaff ? ` - ${paymentStaff.name}` : ''}`}
        subtitle={editingPaymentId ? 'Update this staff payment' : 'Enter payment details (recorded as a staff expense)'}
        maxWidth="sm"
        mobileFullScreen
        footer={
          <FormActions
            onCancel={() => { setPaymentModalOpen(false); setEditingPaymentId(null); }}
            onSubmit={handleSavePayment}
            submitLabel={editingPaymentId ? 'Save Changes' : 'Record Payment'}
            loading={savingPayment}
          />
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Staff member picker — shown when modal opened from header CTA (no preselected staff) */}
          {!paymentStaff && (
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                Staff Member *
              </InputLabel>
              <Select
                value={paymentStaffId}
                label="Staff Member *"
                onChange={(e) => setPaymentStaffId(e.target.value === '' ? '' : Number(e.target.value))}
                sx={selectSx}
              >
                {staff.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.role ? ` (${s.role})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Payment Type chips */}
          <Box>
            <Typography
              variant="caption"
              sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 1, display: 'block', ml: 0.5 }}
            >
              Payment Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { value: 'SALARY', label: 'Salary', color: 'var(--text-link)' },
                { value: 'ADVANCE', label: 'Advance', color: 'var(--status-warn)' },
                { value: 'BONUS', label: 'Bonus', color: theme.success || '#4CAF50' },
                { value: 'OVERTIME', label: 'Overtime', color: '#9C27B0' }, /* basil-todo */
                { value: 'DEDUCTION', label: 'Deduction', color: theme.danger || '#D99982' },
              ].map((opt) => {
                const isActive = paymentType === opt.value;
                return (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => setPaymentType(opt.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.813rem',
                      minHeight: 36,
                      px: 0.5,
                      backgroundColor: isActive ? `${opt.color}20` : theme.gray?.[100] || '#F0F0F0',
                      color: isActive ? opt.color : theme.gray?.[500] || '#666',
                      border: isActive ? `1.5px solid ${opt.color}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Amount */}
          <FormInput
            label="Amount *"
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value ? Number(e.target.value) : '')}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              sx: { fontSize: '1.25rem', fontWeight: 600 },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 56,
              },
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {/* Payment Mode (vendor-configurable; supports inline add) */}
          <PaymentModeSelect
            value={paymentMode}
            onChange={(v) => setPaymentMode(v)}
            label="Payment Mode"
            fullWidth
          />

          {/* Salary Month + Payment Date */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <FormInput
              label="For Month *"
              type="month"
              value={paymentSalaryMonth}
              onChange={(e) => {
                setPaymentSalaryMonth(e.target.value);
                setPaymentSalaryWeek('');
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Payroll period this payment covers"
            />
            <FormInput
              label="Paid On *"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Transaction date"
            />
          </Box>

          {/* Salary Frequency + ISO Week (Salary only) */}
          {paymentType === 'SALARY' && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                  Frequency
                </InputLabel>
                <Select
                  value={paymentSalaryFrequency}
                  label="Frequency"
                  onChange={(e) => {
                    const next = e.target.value as 'MONTHLY' | 'WEEKLY';
                    setPaymentSalaryFrequency(next);
                    if (next === 'MONTHLY') setPaymentSalaryWeek('');
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                  <MenuItem value="WEEKLY">Weekly</MenuItem>
                </Select>
              </FormControl>

              {paymentSalaryFrequency === 'WEEKLY' && (
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                    ISO Week *
                  </InputLabel>
                  <Select
                    value={paymentSalaryWeek}
                    label="ISO Week *"
                    onChange={(e) => setPaymentSalaryWeek(e.target.value === '' ? '' : Number(e.target.value))}
                    sx={selectSx}
                  >
                    {isoWeeksInMonth(paymentSalaryMonth).map((w) => (
                      <MenuItem key={w.week} value={w.week}>
                        {w.rangeLabel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {/* Description */}
          <FormInput
            label="Notes (optional)"
            value={paymentDescription}
            onChange={(e) => setPaymentDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={3}
            placeholder="e.g. March salary, Festival bonus"
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default StaffPayments;
